import { YStack } from '@/components/ui/layout/layout';
import { SettingsSection } from '@/features/settings/settings-section';

import { teamApis } from '../team.query';
import { type MyTeam } from '../team.types';

import { TeamAppsGroup } from './TeamEnforcedExclusionsCard';
import {
  platformGap,
  singlePlatformWarning,
  TeamPlatformWarning,
} from './TeamPlatformWarning';

const inactivePlatformWarning = (apps: MyTeam['enforced_allowed_apps']) => {
  if (apps.length === 0) {
    return 'Not applied until you add an app.';
  }
  const gap = platformGap(apps);
  return gap && singlePlatformWarning(gap);
};

type TeamInclusionCardProps = {
  teamId: string;
  mode: MyTeam['enforced_app_access_mode'];
  apps: MyTeam['enforced_allowed_apps'];
};

export const TeamInclusionCard = ({
  teamId,
  mode,
  apps,
}: TeamInclusionCardProps) => {
  const warning = mode === 'allow' ? inactivePlatformWarning(apps) : null;

  return (
    <YStack className="w-full gap-3">
      <SettingsSection.GroupHeader>Allowed Apps</SettingsSection.GroupHeader>
      <SettingsSection.Group>
        <SettingsSection.Item direction="horizontal">
          <SettingsSection.ItemHeader>
            <SettingsSection.ItemLabel>
              Only capture allowed apps
            </SettingsSection.ItemLabel>
            <SettingsSection.ItemDescription>
              When on, these are the only apps Littlebird captures for team
              members, on the matching OS.
            </SettingsSection.ItemDescription>
          </SettingsSection.ItemHeader>
          <SettingsSection.Switch
            checked={mode === 'allow'}
            onCheckedChange={(checked) =>
              teamApis.updateDataControls(teamId, {
                enforced_app_access_mode: checked ? 'allow' : 'block',
              })
            }
          />
        </SettingsSection.Item>
        <SettingsSection.Item direction="vertical">
          {warning && <TeamPlatformWarning message={warning} />}
          <TeamAppsGroup
            teamId={teamId}
            list="inclusions"
            apps={apps}
            emptyLabel="No allowed apps for the team"
            disabled={mode !== 'allow'}
          />
        </SettingsSection.Item>
      </SettingsSection.Group>
    </YStack>
  );
};
